import { useState } from 'react';
import AppIcon from '../../components/AppIcon';
import TemplateCard from './components/TemplateCard';
import TemplateEditor from './components/TemplateEditor';
import TemplatePreview from './components/TemplatePreview';
import { formatNumber } from '../../utils/formatNumber';

const EmailTemplateManagement = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showDemoNotification, setShowDemoNotification] = useState(true);

  const categories = [
    { id: 'all', label: 'جميع القوالب', icon: 'Mail' },
    { id: 'booking', label: 'تأكيد الحجز', icon: 'CheckCircle' },
    { id: 'photographer', label: 'تنبيه المصور', icon: 'Camera' },
    { id: 'payment', label: 'تذكير الدفع', icon: 'DollarSign' },
    { id: 'account', label: 'تحديثات الحساب', icon: 'User' }
  ];

  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: 'تأكيد حجز جديد - عميل',
      category: 'booking',
      description: 'إيميل تأكيد يُرسل للعميل عند إتمام الحجز',
      subject: 'تأكيد حجزك مع {{photographer_name}}',
      body: 'مرحباً {{customer_name}},\n\nشكراً لحجزك جلسة تصوير {{service_type}} مع {{photographer_name}}.\n\nتفاصيل الحجز:\n- التاريخ: {{booking_date}}\n- الوقت: {{booking_time}}\n- الموقع: {{location}}\n- السعر: {{price}} ريال\n\nرقم الحجز: {{booking_id}}\n\nمع تحيات فريق مشهد',
      lastModified: '2026-01-10',
      usageCount: 245,
      status: 'active'
    },
    {
      id: 2,
      name: 'تنبيه حجز جديد - مصور',
      category: 'photographer',
      description: 'إيميل تنبيه يُرسل للمصور عند استلام طلب حجز جديد',
      subject: 'طلب حجز جديد من {{customer_name}}',
      body: 'مرحباً {{photographer_name}},\n\nلديك طلب حجز جديد!\n\nتفاصيل العميل:\n- الاسم: {{customer_name}}\n- الخدمة: {{service_type}}\n- التاريخ المطلوب: {{booking_date}}\n- الوقت: {{booking_time}}\n\nيرجى مراجعة الطلب والرد في أقرب وقت.\n\nرقم الحجز: {{booking_id}}',
      lastModified: '2026-01-09',
      usageCount: 198,
      status: 'active'
    },
    {
      id: 3,
      name: 'تذكير دفع معلق',
      category: 'payment',
      description: 'تذكير للعميل بوجود دفعة معلقة',
      subject: 'تذكير: دفعة معلقة للحجز {{booking_id}}',
      body: 'مرحباً {{customer_name}},\n\nنذكرك بوجود دفعة معلقة لحجزك رقم {{booking_id}}.\n\nالمبلغ المطلوب: {{amount}} ريال\nتاريخ الاستحقاق: {{due_date}}\n\nيرجى إتمام الدفع لتأكيد حجزك.\n\nشكراً لك',
      lastModified: '2026-01-08',
      usageCount: 87,
      status: 'active'
    },
    {
      id: 4,
      name: 'تأكيد استلام الدفع',
      category: 'payment',
      description: 'إيميل تأكيد يُرسل عند استلام الدفع بنجاح',
      subject: 'تم استلام دفعتك بنجاح',
      body: 'مرحباً {{customer_name}},\n\nتم استلام دفعتك بنجاح!\n\nتفاصيل الدفع:\n- المبلغ: {{amount}} ريال\n- رقم العملية: {{transaction_id}}\n- التاريخ: {{payment_date}}\n\nحجزك مؤكد الآن.\n\nشكراً لك',
      lastModified: '2026-01-07',
      usageCount: 312,
      status: 'active'
    },
    {
      id: 5,
      name: 'تحديث حالة الحجز',
      category: 'account',
      description: 'إيميل يُرسل عند تغيير حالة الحجز',
      subject: 'تحديث حالة حجزك {{booking_id}}',
      body: 'مرحباً {{customer_name}},\n\nتم تحديث حالة حجزك رقم {{booking_id}}.\n\nالحالة الجديدة: {{new_status}}\n\nللمزيد من التفاصيل، يرجى زيارة لوحة التحكم.\n\nمع تحيات فريق مشهد',
      lastModified: '2026-01-06',
      usageCount: 156,
      status: 'active'
    },
    {
      id: 6,
      name: 'تذكير موعد الجلسة',
      category: 'booking',
      description: 'تذكير يُرسل قبل موعد الجلسة بـ 24 ساعة',
      subject: 'تذكير: جلسة التصوير غداً',
      body: 'مرحباً {{customer_name}},\n\nنذكرك بموعد جلسة التصوير غداً:\n\n- الوقت: {{booking_time}}\n- الموقع: {{location}}\n- المصور: {{photographer_name}}\n\nنتطلع لرؤيتك!',
      lastModified: '2026-01-05',
      usageCount: 423,
      status: 'active'
    }
  ]);

  const filteredTemplates = templates?.filter(template => {
    const matchesCategory = categoryFilter === 'all' || template?.category === categoryFilter;
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         template?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setIsEditing(true);
    setIsPreviewing(false);
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setIsPreviewing(true);
    setIsEditing(false);
  };

  const handleDuplicate = (template) => {
    const newTemplate = {
      ...template,
      id: templates?.length + 1,
      name: `${template?.name} (نسخة)`,
      usageCount: 0,
      lastModified: new Date()?.toISOString()?.split('T')?.[0]
    };
    setTemplates([...templates, newTemplate]);
  };

  const handleSave = (updatedTemplate) => {
    setTemplates(templates?.map(t => t?.id === updatedTemplate?.id ? updatedTemplate : t));
    setIsEditing(false);
    setSelectedTemplate(null);
  };

  const handleSimulateSend = (template) => {
    // محاكاة إرسال الإيميل
    const updatedTemplate = {
      ...template,
      usageCount: template?.usageCount + 1,
      lastModified: new Date()?.toISOString()?.split('T')?.[0]
    };
    setTemplates(templates?.map(t => t?.id === template?.id ? updatedTemplate : t));
    
    // إظهار رسالة نجاح
    alert(`✅ تم محاكاة إرسال الإيميل بنجاح!\n\nالقالب: ${template?.name}\nالحالة: تم الإرسال (وضع تجريبي)\nالمستلم: test@example.com`);
  };

  const handleClose = () => {
    setIsEditing(false);
    setIsPreviewing(false);
    setSelectedTemplate(null);
  };

  const stats = [
    {
      label: 'إجمالي القوالب',
      value: formatNumber(templates?.length?.toString()),
      icon: 'Mail',
      color: 'var(--color-primary)'
    },
    {
      label: 'القوالب النشطة',
      value: formatNumber(templates?.filter(t => t?.status === 'active')?.length?.toString()),
      icon: 'CheckCircle',
      color: 'var(--color-success)'
    },
    {
      label: 'إجمالي الإرسالات',
      value: formatNumber(templates?.reduce((sum, t) => sum + t?.usageCount, 0)?.toString()),
      icon: 'Send',
      color: 'var(--color-accent)'
    }
  ];

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Demo Mode Notification */}
        {showDemoNotification && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AppIcon name="AlertCircle" size={20} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-amber-900 mb-1">الوضع التجريبي مفعّل</h3>
                  <p className="text-sm text-amber-700 leading-relaxed">
                    نظام الإيميلات يعمل حالياً في الوضع التجريبي. جميع عمليات الإرسال سيتم محاكاتها بدون إرسال فعلي للإيميلات. 
                    يمكنك معاينة القوالب واختبار الوظائف بشكل كامل.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDemoNotification(false)}
                className="text-amber-600 hover:text-amber-800 transition-colors"
              >
                <AppIcon name="X" size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">إدارة قوالب الإيميل</h1>
          <p className="text-muted-foreground">إنشاء وتحرير قوالب الإيميلات التلقائية للنظام</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats?.map((stat, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{stat?.label}</p>
                  <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <AppIcon name={stat?.icon} size={24} color={stat?.color} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Search and Filter */}
        <div className="bg-card rounded-lg p-6 border border-border mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <AppIcon 
                  name="Search" 
                  size={20} 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder="البحث في القوالب..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pr-10 pl-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
              {categories?.map(cat => (
                <button
                  key={cat?.id}
                  onClick={() => setCategoryFilter(cat?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    categoryFilter === cat?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  <AppIcon name={cat?.icon} size={18} />
                  <span className="text-sm font-medium">{cat?.label}</span>
                  {cat?.id !== 'all' && (
                    <span className="text-xs bg-background/20 px-2 py-0.5 rounded-full">
                      {templates?.filter(t => t?.category === cat?.id)?.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates?.map(template => (
            <TemplateCard
              key={template?.id}
              template={template}
              onEdit={handleEdit}
              onPreview={handlePreview}
              onDuplicate={handleDuplicate}
              onSimulateSend={handleSimulateSend}
            />
          ))}
        </div>

        {filteredTemplates?.length === 0 && (
          <div className="text-center py-12">
            <AppIcon name="Mail" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">لا توجد قوالب مطابقة للبحث</p>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {isEditing && selectedTemplate && (
        <TemplateEditor
          template={selectedTemplate}
          onSave={handleSave}
          onClose={handleClose}
        />
      )}

      {/* Preview Modal */}
      {isPreviewing && selectedTemplate && (
        <TemplatePreview
          template={selectedTemplate}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default EmailTemplateManagement;